import { Divider, Flex, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconFileInvoice } from "@tabler/icons-react";
import { Loading, PageHeader } from "components";
import PageContent from "components/layouts/PageContent";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomText from "components/ui/CustomText/CustomText";
import GroupButton, {
  ActionButton,
} from "components/ui/GroupButton/GroupButton";
import { DEBT_STATUS_COLOR, DEBT_STATUS_LABEL } from "constants/common/debt";
import { ReactNode, useCallback, useRef } from "react";
import { formatCurrency, formatDate } from "utils/helpers/format";
import { useParams } from "react-router-dom";
import { DEBT_PAGE } from "routes/route.constant";
import { useDebtDetail } from "utils/hooks/request/useDebt";
import { useUserMap } from "utils/hooks/request/useUsers";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { DEBT_PAGE_TITLE, FORM_IDS } from "../config";
import PaymentJournalsTable from "../components/PaymentJournalsTable/PaymentJournalsTable";
import ReceiptCreateForm from "../components/ReceiptCreateForm/ReceiptCreateForm";
import {
  SALES_ORDER_STATUS_COLOR,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";

// ─── sub-components ─────────────────────────────────────────────────────────

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value ?? "—"}
    </Text>
  </Stack>
);

const SectionTitle = ({ children }: { children: string }) => (
  <CustomText variant="section-title">{children}</CustomText>
);

// ─── page ────────────────────────────────────────────────────────────────────

const DetailDebt = () => {
  const { id } = useParams<{ id: string }>();
  const { debt, isLoading, mutate } = useDebtDetail(id);
  const userMap = useUserMap();
  const { openCrudModal, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const isFullyPaid = !!debt && debt.paidAmount >= debt.totalAmount;

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenReceiptModal = useCallback(() => {
    if (!debt) return;
    const modalId = openCrudModal(
      <CustomText variant="section-title">Tạo phiếu thu công nợ</CustomText>,
      {
        mode: "create",
        formId: FORM_IDS.CREATE_RECEIPT,
        children: (
          <ReceiptCreateForm
            saleOrderId={debt.saleOrderId}
            maxAmount={debt.totalAmount - debt.paidAmount}
            formId={FORM_IDS.CREATE_RECEIPT}
            mutate={mutate}
            onClose={handleCloseModal}
          />
        ),
        submitText: "Tạo phiếu thu",
      },
      650,
    );
    activeModalId.current = modalId;
  }, [debt, openCrudModal, handleCloseModal, mutate]);

  const actions: ActionButton[] = [
    {
      key: "receipt",
      label: "Tạo phiếu thu công nợ",
      icon: <IconFileInvoice size={18} />,
      variant: "filled",
      color: "green",
      isLoading: false,
      disabled: isFullyPaid,
      onClick: handleOpenReceiptModal,
      permission: "payment_journal:create",
    },
  ];

  return (
    <Stack gap="sm">
      <PageHeader
        breadcrumbs={[
          { title: DEBT_PAGE_TITLE, href: DEBT_PAGE },
          { title: "Chi tiết công nợ", href: "" },
        ]}
      />

      <PageContent>
        <Loading variant="overlay" visible={isLoading}>
          {debt && (
            <Stack gap="md">
              {/* ── Thông tin công nợ ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Flex justify="space-between" align="center">
                    <SectionTitle>Thông tin công nợ</SectionTitle>
                    <GroupButton actions={actions} />
                  </Flex>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField label="Mã công nợ" value={debt.code} />
                    <InfoField
                      label="Trạng thái"
                      value={
                        <CustomBadge
                          label={DEBT_STATUS_LABEL[debt.status] ?? debt.status}
                          color={DEBT_STATUS_COLOR[debt.status] ?? "gray"}
                        />
                      }
                    />
                    <InfoField
                      label="Ngày tạo"
                      value={formatDate(debt.createdAt)}
                    />
                    <InfoField
                      label="Tổng tiền"
                      value={
                        <Text size="sm" fw={700} c="blue">
                          {formatCurrency(debt.totalAmount)}
                        </Text>
                      }
                    />
                    <InfoField
                      label="Đã thanh toán"
                      value={
                        <Text size="sm" fw={700} c="green">
                          {formatCurrency(debt.paidAmount)}
                        </Text>
                      }
                    />
                    <InfoField
                      label="Còn lại"
                      value={
                        <Text size="sm" fw={700} c="red">
                          {formatCurrency(debt.totalAmount - debt.paidAmount)}
                        </Text>
                      }
                    />
                  </SimpleGrid>
                </Stack>
              </Paper>

              {/* ── Thông tin đơn bán hàng ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Thông tin đơn bán hàng</SectionTitle>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField
                      label="Mã đơn hàng"
                      value={debt.saleOrder.code}
                    />
                    <InfoField
                      label="Trạng thái đơn hàng"
                      value={
                        <CustomBadge
                          label={
                            SALES_ORDER_STATUS_LABEL[debt.saleOrder.status] ??
                            debt.saleOrder.status
                          }
                          color={
                            SALES_ORDER_STATUS_COLOR[debt.saleOrder.status] ??
                            "blue"
                          }
                        />
                      }
                    />
                    <InfoField
                      label="Tổng tiền đơn hàng"
                      value={formatCurrency(debt.saleOrder.totalAmount)}
                    />
                    <InfoField
                      label="Đã thanh toán (đơn hàng)"
                      value={formatCurrency(debt.saleOrder.paidAmount)}
                    />
                    <InfoField
                      label="Ngày xác nhận"
                      value={formatDate(debt.saleOrder.confirmedAt)}
                    />
                  </SimpleGrid>

                  {debt.saleOrder.notes && (
                    <>
                      <Divider />
                      <InfoField label="Ghi chú" value={debt.saleOrder.notes} />
                    </>
                  )}
                </Stack>
              </Paper>

              {/* ── Thông tin khách hàng ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Thông tin khách hàng</SectionTitle>

                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <InfoField
                      label="Tên khách hàng"
                      value={debt.partner.name}
                    />
                    <InfoField
                      label="Mã khách hàng"
                      value={debt.partner.code}
                    />
                    <InfoField
                      label="Số điện thoại"
                      value={debt.partner.phone}
                    />
                    <InfoField
                      label="Mã số thuế"
                      value={debt.partner.taxCode}
                    />
                    <InfoField
                      label="Người phụ trách"
                      value={(() => {
                        // Ưu tiên người phụ trách RIÊNG của đơn bán, không có
                        // mới lấy của đối tác — cùng quy tắc với màn đơn bán.
                        const id =
                          debt.saleOrder?.contactPersonId ??
                          debt.partner.contactPersonId;
                        return id
                          ? userMap[id]?.fullname ?? debt.partner.contactPerson
                          : debt.partner.contactPerson;
                      })()}
                    />
                    <InfoField
                      label="Trạng thái"
                      value={
                        <CustomBadge
                          label={
                            debt.partner.isActive
                              ? "Đang hoạt động"
                              : "Tạm khóa"
                          }
                          color={debt.partner.isActive ? "green" : "red"}
                        />
                      }
                    />
                  </SimpleGrid>
                </Stack>
              </Paper>

              {/* ── Phiếu thu ── */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <SectionTitle>Phiếu thu công nợ</SectionTitle>

                  {debt.paymentJournals.length > 0 ? (
                    <PaymentJournalsTable
                      journals={debt.paymentJournals}
                      mutate={mutate}
                    />
                  ) : (
                    <Text size="sm" c="dimmed">
                      Chưa có phiếu thu nào.
                    </Text>
                  )}
                </Stack>
              </Paper>
            </Stack>
          )}
        </Loading>
      </PageContent>
    </Stack>
  );
};

export default DetailDebt;
